/**
 * Performance optimization utilities
 */

// Image lazy loading with intersection observer
export class LazyImageLoader {
  private static observer: IntersectionObserver | null = null;
  
  static initialize() {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            const src = img.dataset.src;
            if (src) {
              img.src = src;
              img.removeAttribute('data-src');
              img.classList.remove('lazy');
              img.classList.add('loaded');
            }
            this.observer?.unobserve(img);
          }
        });
      },
      {
        rootMargin: '50px 0px',
        threshold: 0.01
      }
    );
  }

  static observe(element: HTMLImageElement) {
    if (this.observer) {
      this.observer.observe(element);
    }
  }

  static disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Route prefetching for critical pages
export function prefetchRoute(route: string) {
  if (typeof window !== 'undefined') {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = route;
    link.as = 'document';
    document.head.appendChild(link);
  }
}

// Prefetch multiple critical routes
export function prefetchCriticalRoutes() {
  if (typeof window !== 'undefined') {
    const criticalRoutes = ['/guides', '/contact', '/faq'];
    
    // Use requestIdleCallback for better performance
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        criticalRoutes.forEach(route => prefetchRoute(route));
      });
    } else {
      // Fallback with timeout
      setTimeout(() => {
        criticalRoutes.forEach(route => prefetchRoute(route));
      }, 2000);
    }
  }
}

// Critical resource preloading
export function preloadResource(href: string, as: string, type?: string) {
  if (typeof window !== 'undefined') {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    if (type) link.type = type;
    document.head.appendChild(link);
  }
}

// Bundle size analysis helper
export function analyzeBundle() {
  if (process.env.NODE_ENV === 'development') {
    const modules = performance.getEntriesByType('navigation');
    
    // Report large resources
    const resources = performance.getEntriesByType('resource');
    const largeResources = resources.filter(r => (r as any).transferSize > 100000);
    if (largeResources.length > 0) {
      console.warn('[Performance] Large resources detected:', largeResources);
    }
  }
}

// Memory leak detection
export function detectMemoryLeaks() {
  if (process.env.NODE_ENV === 'development' && 'memory' in performance) {
    const memory = (performance as any).memory;
    const mb = (bytes: number) => Math.round(bytes / 1048576);
    
    if (memory.usedJSHeapSize / memory.jsHeapSizeLimit > 0.8) {
      console.warn('[Performance] High memory usage detected');
    }
  }
}

// Component render tracking
export function trackComponentRender(componentName: string) {
  if (process.env.NODE_ENV === 'development') {
    const key = `${componentName}-render`;
    performance.mark(`${key}-start`);
    
    return () => {
      performance.mark(`${key}-end`);
      performance.measure(key, `${key}-start`, `${key}-end`);
      const measure = performance.getEntriesByName(key)[0];
      
      if (measure.duration > 16) { // More than one frame
        console.warn(`[Performance] Slow render: ${componentName} took ${measure.duration.toFixed(2)}ms`);
      }
    };
  }
  return () => {};
}

// Network monitoring
export function monitorNetworkRequests() {
  if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.entryType === 'resource') {
          const resource = entry as PerformanceResourceTiming;
          
          // Log slow requests
          if (resource.duration > 2000) {
            console.warn(`[Performance] Slow request: ${resource.name} took ${resource.duration}ms`);
          }
          
          // Log failed requests
          if ((resource as any).transferSize === 0 && resource.name.includes('http')) {
            console.error(`[Performance] Failed request: ${resource.name}`);
          }
        }
      });
    });
    
    observer.observe({ entryTypes: ['resource'] });
    return () => observer.disconnect();
  }
  return () => {};
}

// Core Web Vitals monitoring
export function monitorCoreWebVitals() {
  if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
    // Largest Contentful Paint
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      // LCP tracked: lastEntry.startTime
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // First Input Delay
    new PerformanceObserver((entryList) => {
      entryList.getEntries().forEach((entry: any) => {
        // FID tracked: entry.processingStart - entry.startTime
      });
    }).observe({ entryTypes: ['first-input'] });

    // Cumulative Layout Shift
    new PerformanceObserver((entryList) => {
      let cumulativeScore = 0;
      entryList.getEntries().forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          cumulativeScore += entry.value;
        }
      });
      // CLS tracked: cumulativeScore
    }).observe({ entryTypes: ['layout-shift'] });
  }
}

// Initialize performance monitoring
export function initializePerformanceMonitoring() {
  if (typeof window !== 'undefined') {
    LazyImageLoader.initialize();
    
    // Prefetch critical routes after initial load
    prefetchCriticalRoutes();
    
    if (process.env.NODE_ENV === 'development') {
      analyzeBundle();
      const cleanup = monitorNetworkRequests();
      monitorCoreWebVitals();
      
      // Memory leak detection every 30 seconds
      const memoryInterval = setInterval(detectMemoryLeaks, 30000);
      
      return () => {
        cleanup();
        clearInterval(memoryInterval);
        LazyImageLoader.disconnect();
      };
    }
  }
  return () => {};
}